import jsPDF from 'jspdf';
import {
    versRgb, dateComplete, libelleMois, dessinerEnTete, dessinerPied
} from './planningPdfCommun';

/**
 * LE PDF DES ÉVÉNEMENTS — la liste des dates du département (lot 5.7).
 *
 * IL REMPLACE `ExportDates.js`, ET C'EST LE POINT DU LOT. L'ancienne modale
 * produisait un tableau à deux colonnes sans jamais rien montrer avant de
 * télécharger, portait sa propre copie des huit catégories — huit emojis figés
 * dans le code, que renommer une catégorie ne changeait pas (écart E17) — et
 * appelait `alert()` quand la sélection était vide.
 *
 * Ici, comme pour le calendrier : l'aperçu est CE document (écart E21), les
 * libellés et les couleurs viennent du référentiel, et l'en-tête est la fabrique
 * partagée — donc LES DEUX LOGOS, université puis département (demande de
 * Bertrand du 2026-08-12 : « dans le même esprit que l'export calendrier »).
 *
 * ── CE QUE LA MAQUETTE DESSINE, ET CE QUE LE PAPIER IMPOSE ───────────────────
 * La maquette (`refreshEvtPreview`) reconstitue la page en `div` et estime la
 * capacité d'une page à 42 lignes en portrait, 60 en paysage. Ces nombres sont
 * l'approximation qu'un fichier HTML seul peut faire ; ici la mise en page est
 * MESURÉE en millimètres, donc la pagination annoncée sous l'aperçu est celle du
 * document. C'est toute la raison d'être de l'écart E21.
 *
 * ── TROIS DÉCISIONS QUI S'ÉCARTENT DU DESSIN, ET POURQUOI ────────────────────
 *
 * 1. LE REPÈRE DE L'ÉVÉNEMENT EST UNE PASTILLE, PAS SON ICÔNE (écart E28).
 *    La maquette affiche `<i class="fa-solid fa-…">` à la couleur de la
 *    catégorie. jsPDF ne sait pas dessiner un glyphe FontAwesome : il faudrait
 *    embarquer la police dans le paquet — environ 200 Ko — pour un carré de
 *    3 mm. Le PDF du calendrier dessine déjà des pastilles rondes pour les mêmes
 *    événements, et le document imprimé de l'écart E6 a tranché pareil pour une
 *    raison voisine (police absente au moment d'imprimer). Un événement qui
 *    porte une IMAGE (`logo_url`) la voit dessinée à la place de la pastille :
 *    l'écart E15 promet qu'elle reste affichée partout où elle est renseignée.
 *
 * 2. LE LIBELLÉ PASSE À LA LIGNE, IL N'EST JAMAIS COUPÉ. La maquette tronque en
 *    ellipse (`text-overflow: ellipsis`) parce qu'une ligne de tableau HTML a une
 *    hauteur fixe. Sur le papier, tronquer c'est perdre — et perdre en silence
 *    est précisément le défaut que le lot 5.6 a corrigé sur la liste du
 *    calendrier (écart E23). Un libellé long occupe donc deux lignes.
 *
 * 3. UN MOIS N'EST PAS COUPÉ — SAUF S'IL NE TIENT DANS AUCUNE COLONNE ENTIÈRE.
 *    La règle est celle de la spec §3bis. Mais un mois de quarante événements ne
 *    tient pas dans une colonne : la maquette le laisse alors déborder hors de la
 *    page, sans le dire. Il est ici scindé, avec son nom rappelé en tête de la
 *    colonne suivante suivi de « (suite) ».
 */

const FORMATS = {
    portrait: { largeur: 210, hauteur: 297, colonnes: 1 },
    paysage: { largeur: 297, hauteur: 210, colonnes: 2 }
};

const MARGE_X = 12;
const ECART_COLONNES = 8;

const HAUT_CONTENU = 19;      // sous l'en-tête et son filet
const BAS_MARGE = 12;         // au-dessus du pied de page

const TAILLE_TEXTE = 8.5;
const INTERLIGNE = 3.6;
const PADDING_LIGNE = 1.4;
const LARGEUR_REPERE = 4.2;   // pastille ou image, plus son écart au libellé
const HAUT_BANDEAU_MOIS = 5.2;

/** Hauteur d'une ligne d'événement, selon le nombre de lignes de texte. */
const hauteurItem = (nbLignes) => Math.max(1, nbLignes) * INTERLIGNE + PADDING_LIGNE;

/**
 * Découpe les événements en items mesurés — c'est la seule passe qui appelle
 * jsPDF pour mesurer, et elle doit précéder toute décision de mise en page.
 */
function mesurerItems(pdf, evenements, largeurLibelle) {
    pdf.setFontSize(TAILLE_TEXTE);
    pdf.setFont('helvetica', 'normal');
    return evenements.map(e => {
        // Un libellé de `evenements_config` est borné en base : à 8,5 points dans
        // une colonne de 90 mm il occupe au pire quatre ou cinq lignes, très loin
        // de la hauteur d'une colonne. Aucun cas particulier n'est donc prévu pour
        // un item qui ne tiendrait dans aucune colonne — il n'existe pas.
        const lignes = pdf.splitTextToSize(String(e.libelle || ''), largeurLibelle);
        return { evenement: e, lignes, hauteur: hauteurItem(lignes.length) };
    });
}

/**
 * Regroupe les items en blocs : un item = un bloc à plat, un mois entier = un
 * bloc en mode groupé. C'est le bloc qui est l'unité insécable.
 */
function construireBlocs(items, grouperParMois) {
    if (!grouperParMois) {
        return items.map(item => ({ items: [item], hauteur: item.hauteur }));
    }
    const parMois = new Map();
    items.forEach(item => {
        const cle = String(item.evenement.date).slice(0, 7);
        if (!parMois.has(cle)) parMois.set(cle, []);
        parMois.get(cle).push(item);
    });
    return [...parMois.keys()].sort().map(cle => {
        const groupe = parMois.get(cle);
        return {
            entete: libelleMois(cle),
            items: groupe,
            hauteur: HAUT_BANDEAU_MOIS + groupe.reduce((t, i) => t + i.hauteur, 0)
        };
    });
}

/**
 * LA PAGINATION — on remplit une colonne, puis la suivante, puis une page.
 *
 * Elle est calculée AVANT de dessiner, pour deux raisons : le pied doit écrire
 * « p. 1/3 » dès la première page, et l'écran annonce le nombre de pages sous
 * l'aperçu. Une pagination découverte en dessinant obligerait à revenir corriger
 * une page déjà quittée.
 *
 * @returns {Array} pages -> colonnes -> éléments positionnés
 */
function paginer(blocs, nbColonnes, hauteurColonne) {
    const pages = [];
    let page = null;
    let colonne = null;
    let y = 0;

    const nouvelleColonne = () => {
        if (!page || page.length >= nbColonnes) {
            page = [];
            pages.push(page);
        }
        colonne = [];
        page.push(colonne);
        y = 0;
    };
    const restant = () => hauteurColonne - y;
    const poserBandeau = (libelle) => {
        colonne.push({ type: 'mois', libelle, y });
        y += HAUT_BANDEAU_MOIS;
    };
    const poserItem = (item) => {
        colonne.push({ type: 'evenement', item, y });
        y += item.hauteur;
    };

    nouvelleColonne();

    blocs.forEach(bloc => {
        // Il ne tient pas ici mais tiendrait dans une colonne entière : on change
        // de colonne plutôt que de le couper (règle de la spec §3bis).
        if (bloc.hauteur > restant() && bloc.hauteur <= hauteurColonne) nouvelleColonne();

        if (bloc.hauteur <= restant()) {
            if (bloc.entete) poserBandeau(bloc.entete);
            bloc.items.forEach(poserItem);
            return;
        }

        // Il ne tient dans AUCUNE colonne entière : il faut le scinder. Le nom du
        // mois est rappelé à chaque reprise, suivi de « (suite) » — sans ce rappel,
        // la seconde moitié d'un mois se lit comme appartenant au précédent. On
        // remplit d'abord ce qui reste de la colonne courante : la scission est
        // déjà un pis-aller, y ajouter une demi-colonne blanche serait gratuit.
        const hautEntete = bloc.entete ? HAUT_BANDEAU_MOIS : 0;
        let reste = bloc.items;
        let premier = true;

        while (reste.length) {
            if (restant() < hautEntete + reste[0].hauteur) nouvelleColonne();
            if (bloc.entete) poserBandeau(premier ? bloc.entete : bloc.entete + ' (suite)');
            premier = false;

            const avant = reste.length;
            while (reste.length && reste[0].hauteur <= restant()) {
                poserItem(reste[0]);
                reste = reste.slice(1);
            }
            // Un item plus haut qu'une colonne entière ne « tiendra » jamais : on le
            // pose tel quel plutôt que de tourner en boucle. Le cas n'est pas
            // atteignable avec un libellé borné en base, mais une boucle infinie
            // n'est pas un risque qu'on laisse dépendre d'une contrainte de schéma.
            if (reste.length === avant) {
                poserItem(reste[0]);
                reste = reste.slice(1);
            }
        }
    });

    return pages;
}

/**
 * @param {Object} p
 * @param {Array}  p.evenements   [{id, date:'AAAA-MM-JJ', libelle, couleur, image}]
 *                                déjà RÉSOLUS (couleur du référentiel) et filtrés
 * @param {string} p.orientation  'portrait' (défaut) | 'paysage'
 * @param {boolean} p.grouperParMois
 * @param {string} p.titre, p.sousTitre, p.auteur
 * @param {Object} p.logo           logo du DÉPARTEMENT — {data, width, height} ou null
 * @param {Object} p.logoUniversite logo de l'UNIVERSITÉ, posé à SA GAUCHE — idem
 * @returns {{pdf: jsPDF, pages: number, evenementsImprimes: number, colonnes: number}}
 */
export function genererPdfEvenements({
    evenements = [],
    orientation = 'portrait',
    grouperParMois = false,
    titre = '',
    sousTitre = '',
    auteur = '',
    logo = null,
    logoUniversite = null
}) {
    const format = FORMATS[orientation] || FORMATS.portrait;
    const pdf = new jsPDF({
        orientation: format.colonnes > 1 ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4'
    });

    const nbColonnes = format.colonnes;
    const largeurUtile = format.largeur - MARGE_X * 2;
    const largeurColonne = (largeurUtile - (nbColonnes - 1) * ECART_COLONNES) / nbColonnes;
    const yPied = format.hauteur - 8;
    const hauteurColonne = format.hauteur - BAS_MARGE - HAUT_CONTENU;

    // La largeur de la colonne de dates est MESURÉE sur la plus longue date
    // possible, pas devinée : une valeur en dur déborderait le jour où la taille
    // du texte change.
    pdf.setFontSize(TAILLE_TEXTE);
    pdf.setFont('helvetica', 'bold');
    const largeurDate = pdf.getTextWidth('30/09/2025') + 2.5;
    const largeurLibelle = largeurColonne - largeurDate - LARGEUR_REPERE;

    const tries = [...evenements].sort((a, b) => (a.date < b.date ? -1 : 1));
    const items = mesurerItems(pdf, tries, largeurLibelle);
    const plan = paginer(construireBlocs(items, grouperParMois), nbColonnes, hauteurColonne);
    const pages = Math.max(1, plan.length);

    /* ── DESSIN ────────────────────────────────────────────────────────────── */
    const enTete = () => {
        dessinerEnTete(pdf, {
            largeurPage: format.largeur,
            margeX: MARGE_X,
            logo,
            logoUniversite,
            titre,
            titreParDefaut: 'Événements',
            sousTitre
        });
        // Le filet de la maquette (`.pdf-head` porte un `border-bottom`). Le PDF du
        // calendrier n'en a pas besoin : la bande bleue de ses mois vient juste
        // dessous et sépare déjà. Une liste, elle, commencerait collée au titre.
        pdf.setDrawColor(30, 64, 175);
        pdf.setLineWidth(0.5);
        pdf.line(MARGE_X, HAUT_CONTENU - 3, format.largeur - MARGE_X, HAUT_CONTENU - 3);
    };

    const dessinerBandeauMois = (x, y, libelle) => {
        pdf.setFillColor(239, 246, 255);
        pdf.rect(x, y, largeurColonne, HAUT_BANDEAU_MOIS, 'F');
        pdf.setFontSize(TAILLE_TEXTE);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text(libelle, x + 1.8, y + HAUT_BANDEAU_MOIS - 1.6);
    };

    const dessinerItem = (x, y, item) => {
        const e = item.evenement;
        const base = y + INTERLIGNE - 0.6;

        pdf.setFontSize(TAILLE_TEXTE);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(100, 116, 139);
        pdf.text(dateComplete(e.date), x, base);

        // L'image de l'événement s'il en a une, sinon la pastille de sa couleur.
        const xRepere = x + largeurDate;
        let imagePosee = false;
        if (e.image?.data && e.image.width && e.image.height) {
            const cote = 2.9;
            const ratio = e.image.width / e.image.height;
            const l = ratio >= 1 ? cote : cote * ratio;
            const h = ratio >= 1 ? cote / ratio : cote;
            try {
                pdf.addImage(e.image.data, 'PNG', xRepere + (cote - l) / 2, base - h + 0.4, l, h);
                imagePosee = true;
            } catch {
                imagePosee = false;   // une image illisible retombe sur la pastille
            }
        }
        if (!imagePosee) {
            const rgb = versRgb(e.couleur);
            pdf.setFillColor(rgb.r, rgb.g, rgb.b);
            pdf.circle(xRepere + 1.4, base - 0.9, 1.25, 'F');
        }

        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(30, 41, 59);
        item.lignes.forEach((ligne, i) => {
            pdf.text(ligne, x + largeurDate + LARGEUR_REPERE, base + i * INTERLIGNE);
        });

        // Le filet de séparation, comme la maquette (`border-bottom: 1px #eef2f7`).
        pdf.setDrawColor(238, 242, 247);
        pdf.setLineWidth(0.1);
        pdf.line(x, y + item.hauteur - 0.5, x + largeurColonne, y + item.hauteur - 0.5);
    };

    plan.forEach((page, iPage) => {
        if (iPage > 0) pdf.addPage();
        enTete();
        page.forEach((colonne, iCol) => {
            const x = MARGE_X + iCol * (largeurColonne + ECART_COLONNES);
            colonne.forEach(element => {
                const y = HAUT_CONTENU + element.y;
                if (element.type === 'mois') dessinerBandeauMois(x, y, element.libelle);
                else dessinerItem(x, y, element.item);
            });
        });

        // AUCUN ÉVÉNEMENT RETENU : la page existe quand même, et elle le dit —
        // comme la maquette. Sans ce message, un aperçu vide se lit comme une
        // panne de l'aperçu.
        //
        // ⚠️ Le test porte sur les ITEMS, pas sur le plan. `paginer` ouvre
        // toujours sa première colonne, donc le plan n'est jamais vide : la
        // première version testait `!plan.length` et le message n'a jamais pu
        // s'afficher. Défaut trouvé par le banc d'essai, sur les quatre
        // configurations à zéro événement.
        if (!items.length) {
            pdf.setFontSize(9.5);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(148, 163, 184);
            pdf.text('Aucun événement sélectionné.', format.largeur / 2, HAUT_CONTENU + 10, { align: 'center' });
        }

        dessinerPied(pdf, {
            largeurPage: format.largeur, margeX: MARGE_X, y: yPied,
            page: iPage + 1, pages, auteur
        });
    });

    return { pdf, pages, evenementsImprimes: tries.length, colonnes: nbColonnes };
}
